import { useEffect, useState } from 'react';
import {
  Drawer, Box, Button, Typography, Chip, TextField, MenuItem, Stack, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getFinding, updateFindingWorkflow } from '../../../services/securityhubApi';
import { getGuardDutyFinding, updateGDWorkflow } from '../../../services/guarddutyApi';
import { getMacieFinding, updateMacieWorkflow } from '../../../services/macieApi';
import { engineById } from './engines/registry';
import CspmDetailBody from './CspmDetailBody';
import GuardDutyDetailBody from './GuardDutyDetailBody';
import MacieDetailBody from './MacieDetailBody';
import { severityChipSx, stateChipSx } from './severity';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { CK, FONT_TITLE, FONT_BODY } from '../portalStyles';

const WORKFLOW_STATUSES = ['NEW', 'NOTIFIED', 'SUPPRESSED', 'RESOLVED'];

// GuardDuty surfaces archive/unarchive rather than a workflow-status dropdown:
// NEW/ACTIVE findings are "live" (offer Archive); SUPPRESSED/ARCHIVED are
// archived (offer Unarchive).
const isArchived = (status) => status === 'SUPPRESSED' || status === 'ARCHIVED';

// FindingDetail renders as a right side-panel (AWS Security Hub / GuardDuty
// console style) rather than a centred modal, so users migrating from the AWS
// console get the familiar slide-in finding panel.
export default function FindingDetail({ findingId, engine, open, onClose, onUpdated }) {
  const isGuardDuty = engine === 'guardduty';
  const isMacie = engine === 'macie';
  const [finding, setFinding] = useState(null);
  const [error, setError] = useState(null);
  const [workflow, setWorkflow] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !findingId) return;
    // Drop the previous finding first: without this, opening a second finding
    // keeps the old title, severity chip and body on screen until the new
    // request resolves — a visible flash of the wrong finding.
    setFinding(null);
    setError(null);
    (async () => {
      try {
        let res;
        if (isGuardDuty) res = await getGuardDutyFinding(findingId);
        else if (isMacie) res = await getMacieFinding(findingId);
        else res = await getFinding(findingId);
        const data = res?.data || null;
        setFinding(data);
        setWorkflow(data?.workflowStatus || 'NEW');
        // An empty payload must not leave the body spinning forever now that
        // `!finding && !error` renders the loading block.
        setError(data ? null : 'Could not load this finding.');
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [findingId, open, isGuardDuty, isMacie]);

  // The GuardDuty single-finding endpoint returns the normalized shape, so
  // workflowStatus is NEW/SUPPRESSED; fall back to the raw `archived` flag for
  // safety. CSPM/other engines have no archived concept here.
  const gdArchived = isGuardDuty && (isArchived(finding?.workflowStatus) || !!finding?.archived);
  // Macie archive is Prism-local (no AWS-side archive API); the normalized detail
  // exposes it the same way GuardDuty does — workflowStatus ARCHIVED or raw flag.
  const macieArchived = isMacie && (isArchived(finding?.workflowStatus) || !!finding?.archived);

  const save = async () => {
    setSaving(true);
    try {
      if (isGuardDuty) {
        await updateGDWorkflow({ findingIds: [findingId], archive: !gdArchived });
      } else if (isMacie) {
        await updateMacieWorkflow({ findingIds: [findingId], archive: !macieArchived });
      } else {
        await updateFindingWorkflow([findingId], workflow, note);
      }
      setError(null);
      if (onUpdated) onUpdated();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Engine-aware body; force the GuardDuty body when viewing a GuardDuty finding
  // so it never falls back to CSPM, otherwise default to CSPM.
  const Body = isGuardDuty
    ? GuardDutyDetailBody
    : isMacie
      ? MacieDetailBody
      : (engineById(finding?.engine)?.DetailBody || CspmDetailBody);
  const archived = isMacie ? macieArchived : gdArchived;
  const saveLabel = isGuardDuty
    ? (archived ? 'Unarchive' : 'Archive')
    : isMacie
      ? (archived ? 'Unarchive' : 'Archive in Prism')
      : 'Update workflow';
  // Both CSPM and GuardDuty detail DTOs expose severity as `severity`.
  const severityLabel = finding?.severity;

  const footerNoteSx = { fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body, mb: 1.5 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: CK.surface,
        },
      }}
    >
      {/* Sticky header: title + severity / workflow / record-state badges */}
      <Box sx={{ p: 2.5, backgroundColor: CK.pageBg, borderBottom: `1px solid ${CK.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: FONT_TITLE,
              fontWeight: 600,
              fontSize: '1.15rem',
              color: CK.navy,
              lineHeight: 1.3,
              pr: 1,
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {finding?.title || 'Finding'}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              flexShrink: 0,
              color: CK.body,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': { backgroundColor: CK.hover, color: CK.navy },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {finding && (
          <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ mt: 1.5, gap: 0.75 }}>
            <Chip size="small" label={severityLabel} sx={severityChipSx(severityLabel)} />
            {isGuardDuty ? (
              // GuardDuty has no workflow status — just an archived/not-archived state.
              <Chip size="small" label={archived ? 'Archived' : 'Not archived'} sx={stateChipSx} />
            ) : isMacie ? (
              // Macie archive is Prism-local triage state, not an AWS record state.
              <Chip size="small" label={archived ? 'Archived (Prism)' : 'Active'} sx={stateChipSx} />
            ) : (
              <>
                {finding.workflowStatus && <Chip size="small" label={finding.workflowStatus} sx={stateChipSx} />}
                {finding.recordState && <Chip size="small" label={finding.recordState} sx={stateChipSx} />}
              </>
            )}
          </Stack>
        )}
      </Box>

      {/* Scrollable, sectioned body */}
      <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        {error && <PortalErrorState error={error} />}
        {!finding && !error && <PortalLoading label="Loading finding…" />}
        {finding && <Body finding={finding} />}
      </Box>

      {/* Sticky footer: workflow control + actions */}
      {finding && (
        <Box sx={{ p: 2.5, backgroundColor: CK.pageBg, borderTop: `1px solid ${CK.border}` }}>
          {isGuardDuty ? (
            <Typography sx={footerNoteSx}>
              {archived
                ? 'This finding is archived. Unarchive to move it back to active threats.'
                : 'Archive this finding to remove it from active threats.'}
            </Typography>
          ) : isMacie ? (
            <Typography sx={footerNoteSx}>
              {archived
                ? 'This finding is archived in Prism. Unarchive to move it back to active findings. Archiving here is Prism-local — the AWS Macie console is unaffected.'
                : 'Archive this finding in Prism to remove it from active findings. This only affects Prism — the AWS Macie console is unaffected.'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, rowGap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <TextField
                select label="Workflow status" value={workflow} size="small"
                onChange={(e) => setWorkflow(e.target.value)}
                sx={{ minWidth: 170 }}
              >
                {WORKFLOW_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField
                label="Note (optional)" value={note} size="small"
                onChange={(e) => setNote(e.target.value)}
                sx={{ flex: '1 1 200px', minWidth: 0 }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={onClose}>Close</Button>
            <Button onClick={save} variant="contained" disabled={saving}>
              {saving ? 'Saving…' : saveLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
